import React from 'react';
import { MapPin } from 'lucide-react';
import CustomSelect from './CustomSelect';
import { COLOR_CATEGORIES } from '../constants/colors';
import { PREP_TIME_OPTIONS, EVENT_TAGS, NOTIFICATION_OPTIONS } from '../constants/options';

export default function EventForm({ form, selectedTags, toggleTag, showNotifications = false, prepKey }) {
  return (
    <div className="flex-1 overflow-auto p-4 pb-48 space-y-4">
      {/* Title */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Event Title</label>
        <input
          type="text"
          placeholder="Enter event name"
          value={form.fields.title}
          onChange={(e) => form.setField('title', e.target.value)}
          className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none placeholder-gray-400"
        />
      </div>

      {/* Date & Time */}
      <div className="space-y-3">
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">Date</label>
          <input
            type="date"
            value={form.fields.date}
            onChange={(e) => form.setField('date', e.target.value)}
            className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Start Time</label>
            <input
              type="time"
              value={form.fields.startTime}
              onChange={(e) => form.setField('startTime', e.target.value)}
              className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">End Time</label>
            <input
              type="time"
              value={form.fields.endTime}
              onChange={(e) => form.setField('endTime', e.target.value)}
              className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 focus:border-blue-500 focus:outline-none"
            />
          </div>
        </div>
      </div>

      {/* Location */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Location</label>
        <div className="relative">
          <MapPin size={18} className="absolute left-3 top-3.5 text-gray-400" />
          <input
            type="text"
            placeholder="Add location"
            value={form.fields.location}
            onChange={(e) => form.setField('location', e.target.value)}
            className="w-full bg-white border-2 border-gray-300 text-gray-900 rounded-lg p-3 pl-10 focus:border-blue-500 focus:outline-none placeholder-gray-400"
          />
        </div>
      </div>

      {/* Prep Time */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Time to Get Ready</label>
        <CustomSelect
          key={prepKey || 'prep'}
          options={PREP_TIME_OPTIONS}
          defaultValue={form.fields.prepTime}
          onChange={(val) => form.setField('prepTime', val)}
        />
      </div>

      {/* Color Category */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">Color Category</label>
        <div className="grid grid-cols-3 gap-3">
          {COLOR_CATEGORIES.map(({ color, label }) => (
            <button
              key={color}
              onClick={() => form.setField('color', color)}
              className={`${color} rounded-lg p-3 border-2 transition-colors text-xs font-medium text-gray-900 ${
                form.fields.color === color
                  ? 'border-gray-900 ring-2 ring-gray-900'
                  : 'border-gray-300 hover:border-gray-900'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Priority */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-3">Priority</label>
        <div className="flex flex-wrap gap-2">
          {EVENT_TAGS.map(tag => {
            const label = tag.label || tag;
            const value = tag.value || tag;
            return (
              <button
                key={value}
                onClick={() => toggleTag(value)}
                className={`px-3 py-2 border-2 rounded-lg text-sm font-medium transition-colors ${
                  selectedTags.includes(value)
                    ? value === 'essential'
                      ? 'bg-red-100 border-red-300 text-red-700'
                      : value === 'aware'
                        ? 'bg-amber-100 border-amber-300 text-amber-700'
                        : 'bg-green-100 border-green-300 text-green-700'
                    : 'bg-gray-100 border-gray-300 text-gray-700 hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      {showNotifications && (
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-3">Notifications</label>
          <div className="space-y-2">
            {NOTIFICATION_OPTIONS.map(({ label, defaultChecked }) => (
              <label key={label} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-700">{label}</span>
                <input type="checkbox" defaultChecked={defaultChecked} className="w-5 h-5" />
              </label>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
